'use strict'

/* -----------------------------------------------------------------------------
 * dependencies
 * -------------------------------------------------------------------------- */

// 3rd party
import React, { useEffect, useState } from 'react'
import { useParams } from 'react-router'
import moment from 'moment'

// walrus
import { useNavigate } from 'react-router-dom'
import FlatList from '@components/kit/flat-list'
import Box from '@components/kit/box'
import { Column, Row } from '@components/kit/grid'
import { useUserStore } from '@store/User'
import { IUserInfo } from '@store/User/types'
import { IMedication } from '@store/Meds/types'
import Pressable from '@components/kit/pressable'
import { BackIcon, CheckMarkPulseIcon } from '@components/kit/icons'
import Loading from '@components/kit/loading'
import Text from '@components/kit/text'
import { titleCase } from '@utils/string-formatters'
import uuidHex from '@utils/uuid-hex'

// lib
import AppShell from '@components/ui/app-shell'
import PageHeader from '@components/ui/page-header'
import { useMedsStore } from '@store/Meds'

/* -----------------------------------------------------------------------------
 * DashboardHeaderColumn
 * -------------------------------------------------------------------------- */

type DashboardHeaderColumnProps = {
  label: string
  width: number
}

export const DashboardHeaderColumn = ({
  label,
  width
}: DashboardHeaderColumnProps) => {
  return (
    <Column
      width={width}
      paddingX='16'
      paddingY='04'
      borderColor='shade03b'
      borderStyle='solid'
      borderLeftWidth={1}
      borderBottomWidth={1}
      bg='white'
    >
      <Text fontSize={12} fontWeight='700' numberOfLines={1} textColor='purple'>
        {label.toUpperCase()}
      </Text>
    </Column>
  )
}

/* -----------------------------------------------------------------------------
 * DashboardHeader
 * -------------------------------------------------------------------------- */

export const DashboardHeader = () => {
  return (
    <Row bg='white'>
      <DashboardHeaderColumn label='date added' width={140} />
      <DashboardHeaderColumn label='log date' width={150} />
      <DashboardHeaderColumn label='source' width={130} />
      <DashboardHeaderColumn label='drug name' width={240} />
      <DashboardHeaderColumn label='status' width={130} />
      <DashboardHeaderColumn label='supply' width={140} />
      <DashboardHeaderColumn label='strength' width={160} />
      <DashboardHeaderColumn label='prescriber' width={160} />
      <DashboardHeaderColumn label='prescriber phone' width={180} />
      <DashboardHeaderColumn label='pharmacy' width={240} />
      <DashboardHeaderColumn label='pharmacy phone' width={170} />
      <DashboardHeaderColumn label='maintenance drug' width={200} />
      <DashboardHeaderColumn label='coinsurance amount' width={210} />
      <DashboardHeaderColumn label='patient pay amount' width={200} />
      <DashboardHeaderColumn label='client amount due' width={215} />
      <DashboardHeaderColumn label='tier level' width={145} />
    </Row>
  )
}

/* -----------------------------------------------------------------------------
 * DashboardRow
 * -------------------------------------------------------------------------- */

type DashboardRowProps = {
  medication: IMedication
}

const DashboardRow = ({ medication }: DashboardRowProps) => {
  const isOTC = medication.drug_type === 'MANUALLY_ADDED'
  const isDisabled = !!medication.end_date_of_service
  return (
    <Row>
      <DashboardColumn
        width={140}
        data={moment(medication.date_of_service).format('MM/DD/YYYY')}
        disabled={isDisabled}
      />
      <DashboardColumn
        width={150}
        data={
          medication.start_date_type === 'SPECIFIC_DATE'
            ? moment(medication.start_date_of_service).format('MM/DD/YYYY')
            : medication.start_date_type.replace(/_/g, ' ')
        }
        disabled={isDisabled}
      />
      <DashboardColumn
        data={isOTC ? 'USER' : 'CLAIM'}
        width={130}
        disabled={isDisabled}
      />
      <DashboardColumn
        data={titleCase(medication.drug_name)}
        width={240}
        disabled={isDisabled}
      />
      <DashboardColumn
        data={isDisabled ? 'STOPPED' : isOTC ? 'STARTED' : 'FILLED'}
        width={130}
        disabled={isDisabled}
        stoped={isDisabled}
      />
      <DashboardColumn
        data={medication.days_supply ? `${medication.days_supply} days` : 'N/A'}
        width={140}
        disabled={isDisabled}
      />
      <DashboardColumn
        data={medication.drug_strength ? `${medication.drug_strength}` : null}
        width={160}
        disabled={isDisabled}
      />
      <DashboardColumn
        data={
          medication.prescribing_physician_last_name
            ? `DR. ${medication.prescribing_physician_last_name}`
            : 'N/A'
        }
        width={160}
        disabled={isDisabled}
      />
      <DashboardColumn
        data={medication.prescribing_physician_phone}
        width={180}
        disabled={isDisabled}
      />
      <DashboardColumn
        data={medication.pharmacy_name || 'N/A'}
        width={240}
        disabled={isDisabled}
      />
      <DashboardColumn
        data={medication.pharmacy_telephone_number || 'N/A'}
        width={170}
        disabled={isDisabled}
      />
      <DashboardColumn width={200} disabled={isDisabled}>
        {medication.maintenance_drug_indicator === 'Y' ? (
          <CheckMarkPulseIcon height={22} width={22} />
        ) : null}
      </DashboardColumn>
      <DashboardColumn
        data={`$ ${Number(medication.coinsurance_amount).toFixed(2)}`}
        width={210}
        disabled={isDisabled}
      />
      <DashboardColumn
        data={`$ ${Number(medication.patient_pay_amount).toFixed(2)}`}
        width={200}
        disabled={isDisabled}
      />
      <DashboardColumn
        data={`$ ${Number(medication.net_amount_due).toFixed(2)}`}
        width={215}
        disabled={isDisabled}
      />
      <DashboardColumn
        data={medication.tier_level_code}
        width={145}
        disabled={isDisabled}
      />
    </Row>
  )
}

/* -----------------------------------------------------------------------------
 * DashboardColumn
 * -------------------------------------------------------------------------- */

type DashboardColumnProps = {
  children?: React.ReactNode
  data?: string
  width: number
  disabled?: boolean
  stoped?: boolean
}

export const DashboardColumn = ({
  children,
  data,
  width,
  disabled,
  stoped
}: DashboardColumnProps) => {
  return (
    <Column
      width={width}
      paddingX='16'
      paddingY='04'
      borderColor={'shade03b'}
      borderStyle='solid'
      borderLeftWidth={1}
      borderBottomWidth={1}
      bg={disabled ? 'shade02b' : 'white'}
    >
      {children ? (
        children
      ) : (
        <Text
          fontSize={12}
          numberOfLines={1}
          textColor={stoped ? 'pink' : disabled ? 'shade05b' : 'purple'}
        >
          {data?.toUpperCase()}
        </Text>
      )}
    </Column>
  )
}

/* -----------------------------------------------------------------------------
 * MedicationHistory
 * -------------------------------------------------------------------------- */

const MedicationHistory = () => {
  const navigate = useNavigate()
  const { userId } = useParams()
  const { getUserInfo } = useUserStore.getState()
  const [user, setUser] = useState<IUserInfo | null>(null)

  const goBack = () => {
    navigate(`/chat/${userId}`)
  }

  const { getMedicationsPage } = useMedsStore.getState()
  const [results, setResults] = useState<IMedication[]>([])
  const [page, setPage] = useState(0)
  const [hasNextPage, setHasNextPage] = useState(false)
  const [isLoading, setIsLoading] = useState(true)
  const [isFetchingNextPage, setIsFetchingNextPage] = useState(false)

  useEffect(() => {
    setIsLoading(true)
    getMedicationsPage(parseInt(userId), 0).then(response => {
      setResults(response?.results ?? [])
      setPage(response?.page ?? 0)
      setHasNextPage(!!response?.has_next_page)
      setIsLoading(false)
    })
  }, [getMedicationsPage, userId])

  const loadNextPage = async () => {
    if (!hasNextPage || isFetchingNextPage) {
      return
    }
    setIsFetchingNextPage(true)
    const response = await getMedicationsPage(parseInt(userId), page + 1)
    setResults(current => [...current, ...(response?.results ?? [])])
    setPage(response?.page ?? page + 1)
    setHasNextPage(!!response?.has_next_page)
    setIsFetchingNextPage(false)
  }

  useEffect(() => {
    getUserInfo(parseInt(userId)).then(setUser)
  }, [getUserInfo, userId])

  return (
    <AppShell activePage='home' label='medication history'>
      {user && (
        <>
          <Box alignItems='center' flexDirection='row' mb='08'>
            <Pressable alignItems='center' flexDirection='row' onPress={goBack}>
              <BackIcon fill='proAccent' />
            </Pressable>
          </Box>

          <PageHeader
            eyebrow='Clinical operations'
            title={`Medication history, ${user.first_name} ${user.last_name}`}
          />

          {/* the bounded height is what lets the FlatList below window its
           * rows, so it stays even though the design scrolls the page */}
          <Box
            bg='proSurface'
            borderColor='proBorder'
            borderRadius='proXl'
            borderStyle='solid'
            borderWidth={1}
            boxShadow='proTable'
            maxHeight={690}
          >
            {isLoading ? (
              <Box alignItems='center' p='48'>
                <Loading />
              </Box>
            ) : (
              <FlatList
                width='100%'
                overflowX='auto'
                data={results}
                keyExtractor={(item: IMedication) =>
                  item?.auto_id?.toString() || uuidHex()
                }
                renderItem={({ item }) => (
                  <DashboardRow medication={item as IMedication} />
                )}
                onEndReached={loadNextPage}
                stickyHeaderIndices={[0]}
                ListHeaderComponent={<DashboardHeader />}
                initialNumToRender={20}
                onEndReachedThreshold={0.5}
                ListFooterComponent={
                  isFetchingNextPage && (
                    <Box alignItems='center' justifyContent='center' py='40'>
                      <Loading />
                    </Box>
                  )
                }
              />
            )}
          </Box>
        </>
      )}
    </AppShell>
  )
}

export default MedicationHistory
